import { useEffect, useState } from 'react';
import type { EmailLabel, EmailMessage } from '@jarvis/shared';
import { Icon } from '../components/Icon';
import { Resource } from '../components/Resource';
import { toast } from '../components/toast';
import { DemoBadge, EmptyState, LockedNote, Notice, PageHead, Pipeline } from '../components/ui';
import { fmt } from '../lib/format';
import { usePrefs } from '../lib/prefs';
import { replaceHash } from '../lib/router';
import { EmailLive } from './EmailLive';

const LABELS: Record<EmailLabel, { label: string; cls: string }> = {
  responder: { label: 'Responder', cls: 'badge-accent' },
  importante: { label: 'Importante', cls: 'badge-warn' },
  informativo: { label: 'Informativo', cls: 'badge-info' },
  newsletter: { label: 'Newsletter', cls: 'badge-off' }
};
type Filter = 'todos' | EmailLabel;
const FILTERS: [Filter, string][] = [['todos', 'Todos'], ...Object.entries(LABELS).map(([k, v]) => [k as EmailLabel, v.label] as [Filter, string])];

function Reader({ m, onBack }: { m: EmailMessage | undefined; onBack: () => void }) {
  if (!m) return <EmptyState icon="mail" title="Selecione uma mensagem" text="A mensagem aparece aqui, com a classificação sugerida e o motivo." compact />;
  const lb = LABELS[m.label];
  return (
    <div className="mail-reader">
      <button type="button" className="btn btn-sm btn-ghost back-btn" onClick={onBack}><Icon name="arrowLeft" />Voltar à caixa de entrada</button>
      <div className="chips"><span className={`badge ${lb.cls}`}>{lb.label}</span><DemoBadge /></div>
      <h2>{m.subject}</h2>
      <div className="mail-headers">
        <span><strong>{m.from.name}</strong> &lt;{m.from.address}&gt;</span>
        <span>{fmt.day(m.receivedAt)}, {fmt.time(m.receivedAt)}</span>
      </div>
      <Notice icon="spark"><strong>Classificação sugerida:</strong> {m.reason}</Notice>
      <div className="mail-body">{m.body}</div>
      {m.draft ? (
        <section className="draft" aria-label="Rascunho preparado">
          <div className="chips"><span className="badge badge-accent"><Icon name="edit" />Rascunho preparado</span><DemoBadge /></div>
          <Pipeline read prepare />
          <div className="draft-body">{m.draft}</div>
          <div className="chips">
            <button type="button" className="btn btn-sm" onClick={() => toast('Na demonstração, os rascunhos são fictícios. Troque para Dados reais para usar o seu Gmail.', 'edit')}><Icon name="edit" />Editar rascunho</button>
            <button type="button" className="btn btn-sm btn-primary" disabled title="Envio não implementado nesta etapa"><Icon name="lock" />Enviar</button>
          </div>
        </section>
      ) : (
        <div className="chips"><button type="button" className="btn btn-sm" onClick={() => toast('Na demonstração, nada é preparado. Troque para Dados reais para usar o seu Gmail.', 'spark')}><Icon name="edit" />Preparar resposta</button></div>
      )}
      <LockedNote>Envio, arquivamento e exclusão não existem nesta etapa. Quando existirem, cada envio exigirá sua confirmação explícita.</LockedNote>
    </div>
  );
}

function Mailbox({ msgs, initial }: { msgs: EmailMessage[]; initial: string | null }) {
  const [selected, setSelected] = useState<string | null>(initial);
  const [filter, setFilter] = useState<Filter>('todos');

  useEffect(() => { setSelected(initial); }, [initial]);

  const select = (id: string | null) => {
    setSelected(id);
    replaceHash(id ? `#/email/${encodeURIComponent(id)}` : '#/email');
  };
  const current = msgs.find((m) => m.id === selected);
  const visible = msgs.filter((m) => filter === 'todos' || m.label === filter);

  return (
    <div className={`mail ${current ? 'reading' : ''}`}>
      <section className="card mail-list-card">
        <div className="chips mb-16" role="group" aria-label="Filtrar">
          {FILTERS.map(([k, l]) => <button key={k} type="button" className="chip" aria-pressed={k === filter} onClick={() => setFilter(k)}>{l}</button>)}
        </div>
        <div className="mail-list">
          {visible.map((m) => (
            <button key={m.id} type="button" className={`mail-item ${m.unread ? 'unread' : ''}`} aria-current={m.id === selected} onClick={() => select(m.id)}>
              <span className="from"><span>{m.from.name}</span><span className="time">{fmt.smart(m.receivedAt)}</span></span>
              <span className="subject">{m.subject}</span>
              <span className="snippet">{m.snippet}</span>
            </button>
          ))}
          {visible.length === 0 && <EmptyState icon="inbox" title="Nada neste filtro" compact />}
        </div>
      </section>
      <section className="card mail-reader-card" aria-live="polite"><Reader m={current} onBack={() => select(null)} /></section>
    </div>
  );
}

export function Email({ params }: { params: string[] }) {
  const { stateMode } = usePrefs();
  const demo = stateMode === 'demo';
  return (
    <div className="page">
      <PageHead title="E-mail" lead="Leitura e triagem primeiro; rascunhos só quando você pedir; nada é enviado pelo Jarvis." />
      {demo
        ? (
          <Resource name="email" skeleton="list" skeletonCount={6}
            empty={{ icon: 'inbox', title: 'Sem e-mails de demonstração', text: 'Troque para Dados reais para ver o seu Gmail.' }}>
            {(msgs) => <Mailbox msgs={msgs} initial={params[0] ?? null} />}
          </Resource>
        )
        : <EmailLive params={params} />}
    </div>
  );
}